/**
 * Workflow Service
 *
 * Connects to Flight Director orchestration sessions via the Backend proxy.
 * Transforms session data into the Workflow/Step shapes the UI expects.
 *
 * Flagging persists in AMC (amc.flagged_steps) via /api/flagged-steps.
 */

import { apiFetch } from './api';
import {
  calculateEstCost,
  calculateSpeedMultiplier,
  calculateNetSavings,
} from '../data/mockData';

// ---------- Data transform ----------

/**
 * Map a Flight Director SessionSummary → Client Workflow shape.
 *
 * The list endpoint returns flat fields (`status`, `total_steps`, …) but the
 * detail endpoint nests most runtime fields under `current_plan.*`. We merge
 * both shapes so callers don't care which one they got.
 */
function toWorkflow(session) {
  const plan = session.current_plan || session.plan || {};

  // Coalesce each field: top-level flat (list shape) first, plan nested second.
  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null);

  const rawStatus = pick(session.status, plan.status, plan.goal_status);
  // FD reports a launch that has been accepted but not yet dispatched as
  // 'queued' (spec 021). The AMC UI has no dedicated "queued" bucket — it
  // surfaces these in the "pending" KPI card and the ?status=pending filter —
  // so normalize queued → pending here. Without this, queued runs are counted
  // by nothing and appear to vanish until the dispatcher picks them up.
  const normalizedStatus = rawStatus === 'queued' ? 'pending' : rawStatus;
  const status = session.input_required
    ? 'human_intervention'
    : session.paused
      ? 'paused'
      : normalizedStatus || 'pending';

  const currentStep = pick(session.current_step, plan.current_step) || 0;
  const totalSteps = pick(session.total_steps, plan.total_steps) || 0;
  const startedAt = pick(session.created_at, plan.created_at) || new Date().toISOString();
  const updatedAt = pick(session.updated_at, plan.updated_at) || new Date().toISOString();
  const elapsedMs = pick(session.elapsed_ms, plan.elapsed_ms) || 0;
  const totalTokens = pick(session.total_tokens, plan.total_tokens) || 0;
  const agentTokens = pick(session.agent_tokens, plan.agent_tokens) || 0;
  const hostTokens = pick(session.host_tokens, plan.host_tokens) || 0;
  const humanInterventions = pick(session.human_interventions, plan.human_interventions) || 0;

  const name =
    session.name || plan.name || session.goal || plan.goal ||
    `Session ${session.context_id.slice(0, 8)}`;
  const description =
    session.description || plan.description || session.goal || plan.goal || '';
  const type = session.workflow_name || session.type || plan.type || 'General';

  return {
    id: session.context_id,
    name,
    description,
    type,
    workflowId: session.workflow_id || null,
    workflowName: session.workflow_name || null,
    startedByType: session.started_by_type || 'unknown',  // (020)
    startedByName: session.started_by_name || null,       // (020)
    status,
    currentStep,
    totalSteps,
    startedAt,
    updatedAt,
    humanBaseline: elapsedMs ? (elapsedMs / 3600000) * 10 : 1,
    grossValue: totalTokens ? (totalTokens * 0.003 / 1000) * 50 : 0,
    totalTokens,
    agentTokens,
    hostTokens,
    humanInterventions,
    agents: (session.agents || []).map((a, i) => ({
      id: a.name || `agent-${i}`,
      name: a.name || `Agent ${i + 1}`,
      status: a.status || 'pending',
      description: a.description || '',
    })),
    steps: [], // populated by getWorkflow / getWorkflowSteps when detail is fetched
  };
}

/**
 * Map a Flight Director plan task → Client WorkflowStep shape.
 *
 * FD fields: task_id, task_description, recommended_agent, agent_id,
 *            output (Dict), state, started_at, updated_at, completed_at,
 *            action, reasoning, activity_log
 */
function toStep(task, index) {
  const taskId = task.task_id || task.id || `step-${index}`;
  const agentName = task.recommended_agent || task.agent_name || task.agent || 'Orchestrator';
  const state = mapTaskState(task.state);

  // FD stores output as { text: "...", summary: "..." } or other structured dicts.
  // Prefer the short summary for the "What it delivered" card; fall back to text.
  let delivered = null;
  if (task.output) {
    if (typeof task.output === 'string') delivered = task.output;
    else if (task.output.summary) delivered = task.output.summary;
    else if (task.output.text) delivered = task.output.text;
    else delivered = JSON.stringify(task.output);
  } else if (task.result) {
    delivered = typeof task.result === 'string' ? task.result : JSON.stringify(task.result);
  }

  // FD tasks don't have completed_at — use updated_at when task is terminal
  const completedAt =
    task.completed_at ||
    ((state === 'completed' || state === 'failed') ? task.updated_at || null : null);

  // Prefer the short task_name (set by the planner / derived from the first
  // sentence of the description). Falls back to the long description for older
  // sessions that pre-date the task_name field.
  const displayName =
    task.task_name ||
    task.name ||
    task.task_description ||
    task.description ||
    task.title ||
    `Step ${index + 1}`;

  const evalPassed = task.output && typeof task.output === 'object' && 'eval_passed' in task.output
    ? task.output.eval_passed
    : null;
  const evalScore = task.output && typeof task.output === 'object' && task.output.eval_score != null
    ? Number(task.output.eval_score)
    : null;
  const evalReason = task.output && typeof task.output === 'object'
    ? (task.output.eval_reason || null)
    : null;

  return {
    id: taskId,
    name: displayName,
    description: task.task_description || task.description || null,
    status: state,
    agentId: task.agent_id || agentName,
    agentName,
    startedAt: task.started_at || null,
    completedAt,
    action: task.action || null,
    delivered,
    reasoning: task.reasoning || task.error_message || null,
    activityLog: Array.isArray(task.activity_log) ? task.activity_log : [],
    evalPassed,
    evalScore,
    evalReason,
  };
}

function mapTaskState(state) {
  const map = {
    pending: 'pending',
    running: 'running',
    completed: 'completed',
    failed: 'failed',
    cancelled: 'failed',
    input_required: 'running',
    paused: 'pending',
  };
  return map[state] || 'pending';
}

// ---------- Service functions ----------

/**
 * Fetch all workflows (orchestration sessions).
 * @returns {Promise<Workflow[]>}
 */
export async function getWorkflows(opts = {}) {
  // Optional filters (spec 020):
  //   runningOnly: bool       — only sessions in the running set
  //   startedByType: string   — operator | trigger | webhook | unknown
  // Both are forwarded as query params to the FD endpoint.
  const params = new URLSearchParams();
  if (opts.runningOnly) params.set('running_only', 'true');
  if (opts.startedByType) params.set('started_by_type', opts.startedByType);
  const qs = params.toString();
  const path = qs
    ? `/api/fd/api/orchestration/sessions?${qs}`
    : '/api/fd/api/orchestration/sessions';
  try {
    const data = await apiFetch(path);
    const sessions = Array.isArray(data) ? data : data?.sessions || [];
    return sessions.map(toWorkflow);
  } catch (err) {
    console.error('[workflows] getWorkflows failed:', err);
    return [];
  }
}

/**
 * Fetch a single workflow by ID (session detail).
 * @param {string} id — context_id
 * @returns {Promise<Workflow|null>}
 */
export async function getWorkflow(id) {
  try {
    const session = await apiFetch(`/api/fd/api/orchestration/sessions/${id}`);
    if (!session) return null;

    const wf = toWorkflow(session);

    // Extract steps from the plan
    const plan = session.current_plan || session.plan;
    if (plan?.tasks) {
      wf.steps = plan.tasks.map(toStep);
      wf.totalSteps = wf.steps.length;
      wf.currentStep = wf.steps.filter((s) => s.status === 'completed').length;
    }

    return wf;
  } catch (err) {
    console.error('[workflows] getWorkflow failed:', err);
    return null;
  }
}

/**
 * Fetch steps for a workflow (from session plan).
 * @param {string} workflowId — context_id
 * @returns {Promise<WorkflowStep[]>}
 */
export async function getWorkflowSteps(workflowId) {
  try {
    const session = await apiFetch(`/api/fd/api/orchestration/sessions/${workflowId}`);
    const plan = session?.current_plan || session?.plan;
    if (!plan?.tasks) return [];
    return plan.tasks.map(toStep);
  } catch (err) {
    console.error('[workflows] getWorkflowSteps failed:', err);
    return [];
  }
}

// ---------- Pause / Resume (Flight Director) ----------

/**
 * Pause a running orchestration session.
 *
 * Goes through the FD proxy (Backend strips the `/api/fd/` prefix), matching
 * the base-path convention used by the other session calls in this file.
 *
 * @param {string} contextId — session.context_id (i.e. wf.id)
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export async function pauseSession(contextId) {
  return apiFetch(`/api/fd/api/orchestration/sessions/${contextId}/pause`, {
    method: 'POST',
  });
}

/**
 * Resume a paused orchestration session. The run continues from the last
 * completed step (not step 1) — FD owns the resume point.
 *
 * @param {string} contextId — session.context_id (i.e. wf.id)
 * @returns {Promise<object>} the resumed run result
 */
export async function resumeSession(contextId) {
  return apiFetch(`/api/fd/api/orchestration/sessions/${contextId}/resume`, {
    method: 'POST',
  });
}

// ---------- Flagging (AMC-persisted) ----------

/**
 * Flag a step for human review. Idempotent — the backend returns the existing
 * flag if (workflowId, stepId) is already flagged.
 */
export async function flagStep(workflowId, stepId) {
  const wf = await getWorkflow(workflowId);
  const step = wf?.steps.find((s) => s.id === stepId);
  return apiFetch('/api/flagged-steps', {
    method: 'POST',
    body: JSON.stringify({
      workflowId,
      stepId,
      workflowName: wf?.name || workflowId,
      stepName: step?.name || stepId,
      agentName: step?.agentName || null,
      reasoning: step?.reasoning || null,
      status: step?.status || 'pending',
    }),
  });
}

export async function unflagStep(workflowId, stepId) {
  await apiFetch(
    `/api/flagged-steps/${encodeURIComponent(workflowId)}/${encodeURIComponent(stepId)}`,
    { method: 'DELETE' },
  );
}

export async function getFlaggedItems() {
  return apiFetch('/api/flagged-steps');
}

export async function isFlagged(workflowId, stepId) {
  const items = await getFlaggedItems();
  return items.some((f) => f.workflowId === workflowId && f.stepId === stepId);
}

// Re-export utility functions so components can import from one place
export { calculateEstCost, calculateSpeedMultiplier, calculateNetSavings };
